import { trackProductAction } from '@/libs/product-analytics';
import { useMutation } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import { parseFilenameFromDisposition, triggerBlobDownload } from '@/libs/download';
import { toaster } from '@/components/ui/toaster';

/**
 * Downloads a TikTok mention's MP4 through the backend extractor endpoint
 * and triggers a browser file download.
 */
const downloadTikTokMention = async (mentionId: string): Promise<void> => {
  const response = await api.get(`/dashboard/mentions/${mentionId}/tiktok-download`, {
    responseType: 'blob',
  });

  const disposition =
    (response.headers['content-disposition'] as string | undefined) ||
    (response.headers['Content-Disposition'] as string | undefined);
  const filename = parseFilenameFromDisposition(disposition) ?? `tiktok-${mentionId}.mp4`;

  triggerBlobDownload(response.data, filename);
};

/**
 * React Query mutation hook for downloading a TikTok mention MP4.
 */
export const useDownloadTikTokMention = () => {
  const mutation = useMutation({
    mutationFn: (input: string) =>
      trackProductAction('contents', 'download', () => downloadTikTokMention(input)),
    onError: () => {
      toaster.error({
        title: 'Download failed',
        description: 'Could not download the TikTok video. Please try again.',
      });
    },
  });
  return {
    ...mutation,
    download: mutation.mutate,
    isDownloading: mutation.isPending,
  };
};
